import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { ConfigConstant } from '@shared/constants/config.constant';
import { BaseComponent } from '@shared/components/base-component/base.component';
import { FunctionUtil } from '@shared/utils/function.util';
import { InvoiceType } from '@shared-features/enums/invoice-type.enum';
import { DeliveryNoteStatus } from '@shared-features/enums/delivery-note-status.enum';
import { InvoiceStatus } from '@features/invoices/enums/invoice-status.enum';
import { SectionStateStatus } from '@shared/enums/section-state-status.enum';
import { SharedConstants } from '@shared/model/shared-constants';
import { User } from '@features/users/models/user';
import { SharedApiService } from '@shared-features/services/shared-api.service';
import { Customer } from '@features/customers/models/customer';
import { Shipment } from '@shared-features/models/shipment';
import { DeliveryNote } from '@features/invoices/models/delivery-note';
import { UsersCommonApiService } from '@shared-features/services/users-common-api.service';
import { DeliveryNotesFilterInterface } from '@features/delivery-notes/interfaces/delivery-notes-filter.interface';
import { DeliveryNotesPayloadInterface } from '@features/delivery-notes/interfaces/delivery-notes-payload.interface';
import { MatTableDataSource } from '@angular/material/table';
import { PopoverDirective } from '@shared/directives/popover-directive';

@Component({
  selector: 'app-delivery-notes-page-management',
  templateUrl: './delivery-notes-management-page.component.html',
  styleUrls: ['./delivery-notes-management-page.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('10ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class DeliveryNotesManagementPageComponent extends BaseComponent implements OnInit {
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild('popoverTrigger') popover: PopoverDirective;

  sharedConstants = SharedConstants;
  form: UntypedFormGroup;
  expandMore: boolean = false;
  expandedElement: Shipment = new Shipment();
  //customer Filter
  isFilterSectionEnabled = false;
  customers: Customer[] = [];
  creators: User[] = [];
  filterForm: UntypedFormGroup;
  allStatusCount: number;
  availableToBeIssuedCount: number;
  notReceivedCount: number;
  issuedCount: number;
  customerId: number;
  createdBy: number;
  from: string;
  to: string;
  fromValue: string;
  toValue: string;
  isForExporting: boolean;
  // pagination
  pageSize: number = ConfigConstant.PAGE_SIZE;
  pageIndex = 1;
  offset: number = ConfigConstant.INIT_PAGE_OFFSET;
  totalRowsCount: number;
  deliveryNoteStatus: DeliveryNoteStatus;
  DeliveryNoteStatus = DeliveryNoteStatus;
  InvoicesStatus = InvoiceStatus;

  searchTerm: string = '';

  public dataSource = new MatTableDataSource<DeliveryNote>();
  public displayedColumns: string[] = [
    'customerName',
    'from',
    'to',
    'deliveryNoteId',
    'transportationCost',
    'customerPrice',
    'margin',
    'createdBy',
    'truckNo',
    'driverName',
    'status',
    'invoiceStatus',
    'expand',
  ];

  constructor(
    private sharedApiService: SharedApiService,
    private formBuilder: UntypedFormBuilder,
    private usersCommonApiService: UsersCommonApiService
  ) {
    super();
  }

  ngOnInit(): void {
    this.sectionState = SectionStateStatus.Empty;
    this.initForm();
    this.initFilterForm();
    this.form.get('from').setValue(this.fromValue);
    this.form.get('to').setValue(this.toValue);
  }

  initForm() {
    this.form = this.formBuilder.group({
      from: new UntypedFormControl(null),
      to: new UntypedFormControl(null),
    });
  }

  onValidateButtonClicked() {
    this.getDeliveryNotes();
    this.getAllCustomers();
    this.getAllCreators();
  }

  setupTable(deliveryNotes: DeliveryNote[]) {
    this.dataSource.data = deliveryNotes;
  }

  onSearch(searchTerm: string) {
    this.searchTerm = searchTerm;
    this.getDeliveryNotes();
  }

  onPageIndexChange(index: number) {
    this.pageIndex = index;
    if (index > 1) {
      this.offset = index - 1;
    } else {
      this.offset = 0;
    }
    this.getDeliveryNotes();
  }

  expandRow(shipment: Shipment) {
    if (shipment.deliveryNote.length > 1) {
      this.expandedElement = this.expandedElement?.id === shipment.id ? null : shipment;
    }
  }

  private initFilterForm() {
    this.filterForm = new UntypedFormGroup({
      customer: new UntypedFormControl(SharedConstants.ALL),
      creator: new UntypedFormControl(SharedConstants.ALL),
      customerSearchTerm: new UntypedFormControl(''),
      creatorSearchTerm: new UntypedFormControl(''),
    });
  }

  get filteredCustomers() {
    return this.customers.filter((customer) =>
      customer.dropDownDisplayName
        .toLowerCase()
        .includes(this.filterForm.controls.customerSearchTerm.value?.toLowerCase())
    );
  }

  get filteredCreators() {
    return this.creators.filter((user) =>
      user.dropDownDisplayName
        .toLowerCase()
        .includes(this.filterForm.controls.creatorSearchTerm.value?.toLowerCase())
    );
  }

  handleFilterChanges(event: DeliveryNotesFilterInterface) {
    this.customerId = event.customer;
    this.createdBy = event.creator;
    this.getDeliveryNotes();
    this.popover.close();
  }

  onExportClicked() {
    const payload = this.generateDeliveryNotesPayload();
    this.load(
      this.sharedApiService.exportDeliveyNotes({
        isForExporting: true,
        ...payload,
      }),
      {
        isLoadingTransparent: true,
        successMessagePath: 'SuccessMessages.FileExportedSuccessfully',
      }
    ).subscribe(
      (res) => {
        window.open(res?.fileUri, '_blank');
      },
      (error) => {
        this.sectionState = SectionStateStatus.Empty;
      }
    );
  }

  getDeliveryNotes(): void {
    const payload = this.generateDeliveryNotesPayload();
    this.load(this.sharedApiService.getDeliveryNotes(payload), {
      emptyContentCheck: (deliveryNoteDetails) =>
        !deliveryNoteDetails.shipmentDeliveryNoteList ||
        !deliveryNoteDetails.shipmentDeliveryNoteList?.length,
      isLoadingTransparent: true,
    }).subscribe((deliveryNoteDetails) => {
      this.totalRowsCount = deliveryNoteDetails.count;
      this.allStatusCount = deliveryNoteDetails.allStatusCount;
      this.availableToBeIssuedCount = deliveryNoteDetails.availableToBeIssuedCount;
      this.notReceivedCount = deliveryNoteDetails.notReceivedCount;
      this.issuedCount = deliveryNoteDetails.issuedCount;
      this.setupTable(deliveryNoteDetails.shipmentDeliveryNoteList);
      this.isFilterSectionEnabled = true;
    });
  }

  generateDeliveryNotesPayload(isForExporting?: boolean): DeliveryNotesPayloadInterface {
    const offset = this.offset;
    const limit = this.pageSize;
    const searchTerm = this.searchTerm;
    const startFrom = FunctionUtil.convertDate(this.form.get('from').value);
    const endAt = FunctionUtil.convertDate(this.form.get('to').value);
    const customerId = this.customerId;
    const createdBy = this.createdBy;
    const deliveryNoteStatus = this.deliveryNoteStatus;
    const invoiceType = InvoiceType.Customer;
    return {
      offset,
      limit,
      searchTerm,
      startFrom,
      endAt,
      customerId,
      createdBy,
      deliveryNoteStatus,
      invoiceType,
      isForExporting,
    };
  }

  getAllCustomers() {
    this.load(this.sharedApiService.getAllCustomers(), { fetchOnce: true }).subscribe(
      (customers) => {
        this.customers = customers;
      }
    );
  }

  getAllCreators() {
    this.load(this.usersCommonApiService.getAllUsers(), { fetchOnce: true }).subscribe((users) => {
      this.creators = users;
    });
  }
}
